import React, { FC, Fragment, useCallback, useContext, useMemo } from "react";
import classNames, { Argument } from "classnames/dedupe";

import { IImageData } from "./Image.interface";
import { ImageContext } from "./Image.context";
import { useWatchState } from "../../../../hooks";

export interface IImageProps extends IImageData {
  /** Additional classes */
  classes?: {
    root?: Argument;
    ratio?: Argument;
  };
}

export const Image: FC<IImageProps> = (props) => {
  const { src, alt, sources, classes, ratio, content, srcset, sizes, ...rest } =
    props;
  const imageContext = useContext(ImageContext);
  const { wrapper: ImageCustomContextWrapper } = imageContext;

  const currentContent = useMemo(
    () => content || imageContext.content,
    [content, imageContext.content],
  );

  const noContentFallback = useMemo(
    () =>
      currentContent === "person"
        ? imageContext.empty.person
        : imageContext.empty.default,
    [currentContent, imageContext.empty.default, imageContext.empty.person],
  );

  const errorContentFallback = useMemo(
    () =>
      currentContent === "person"
        ? imageContext.error.person
        : imageContext.error.default,
    [currentContent, imageContext.error.default, imageContext.error.person],
  );

  const [curSrc, setCurSrc] = useWatchState({
    value: src || noContentFallback,
  });

  const showErrorImage = useCallback(() => {
    if (curSrc !== errorContentFallback) setCurSrc(errorContentFallback);
  }, [errorContentFallback, setCurSrc, curSrc]);

  const additionalProps = useMemo(
    () => (ImageCustomContextWrapper ? props : {}),
    [ImageCustomContextWrapper, props],
  );

  let RenderComponent = ImageCustomContextWrapper || Fragment;
  if (!ImageCustomContextWrapper) {
    if (sources?.length) {
      RenderComponent = "picture";
    } else if (ratio) {
      RenderComponent = "div";
    }
  }

  const wrapperProps = useMemo(
    () =>
      ratio && ratio !== "none"
        ? {
            className: classNames(
              `ratio ratio-img ratio-${ratio}`,
              classes?.ratio,
            ),
            "data-testid": "Image__RatioClass",
          }
        : {},
    [classes, ratio],
  );

  return (
    <RenderComponent {...wrapperProps} {...additionalProps}>
      {sources?.map(({ srcset: sourceSrcSet, media, type, ...restSource }) => (
        <source
          key={sourceSrcSet}
          srcSet={sourceSrcSet}
          media={media}
          type={type}
          {...(restSource as any)}
        />
      ))}
      <img
        srcSet={srcset}
        sizes={sizes}
        src={curSrc}
        alt={alt}
        className={classNames(classes?.root)}
        data-testid="image-component"
        onError={showErrorImage}
        {...rest}
      />
    </RenderComponent>
  );
};

export default Image;
